/**
 * components/ValidationPanel/ValidationPanelGain.js
 * Composant pour gérer la confirmation des gains suspects
 */
import "./ValidationPanelGain.css";

import DataCollectorForTests from "../../utils/DataCollectorForTests.js";

import logger from "../../utils/logger.js";

export class ValidationPanelGain {
  constructor(state, options = {}) {
    this.state = state;
    this.options = {
      onGainConfirmed: options.onGainConfirmed || null,
      ...options,
    };

    this.element = null;
    this.dataCollector = new DataCollectorForTests();
    this.suspiciousGains = [];
    this.confirmations = new Map(); // fiberReference -> { confirmed: boolean, comment: string }
  }

  /**
   * Initialiser le composant
   */
  async initialize() {
    try {
      this.suspiciousGains = this.detectSuspiciousGains();
      return {
        totalFibers: this.suspiciousGains.length,
        hasData: this.suspiciousGains.length > 0,
      };
    } catch (error) {
      logger.error(
        {
          context: "panel-gain-initialization",
          error: error.message,
          stack: error.stack,
          suspiciousGainsCount: this.suspiciousGains?.length || 0,
          hasDataCollector: !!this.dataCollector,
        },
        "Erreur initialisation ValidationPanelGain"
      );
      throw error;
    }
  }

  /**
   * Détecter les gains suspects
   * Fibres déclarées occupées mais qui ont des mesures dans pvSuccess/pvEchec
   */
  detectSuspiciousGains() {
    const fibers = this.dataCollector.collectAndPrepare();
    const suspicious = [];

    fibers.forEach((fiber) => {
      // Vérifier si fibre déclarée occupée
      if (fiber.config?.ETAT === "occupee") {
        // Vérifier si elle a des mesures SOR
        const hasMeasurements = !!(fiber.pvReussite?.files?.length > 0 || fiber.pvEchec?.files?.length > 0);

        if (hasMeasurements) {
          suspicious.push({
            id: `gain_${fiber.id}`,
            reference: fiber.referenceShort,
            port: fiber.port,
            currentState: "occupee",
            measurements: {
              pvReussite: !!fiber.pvReussite?.files?.length,
              pvEchec: !!fiber.pvEchec?.files?.length,
              pvReussiteCount: fiber.pvReussite?.files?.length || 0,
              pvEchecCount: fiber.pvEchec?.files?.length || 0,
            },
            fiber: fiber,
          });
        }
      }
    });

    return suspicious;
  }

  /**
   * Rendu principal
   */
  render() {
    if (this.suspiciousGains.length === 0) {
      return this.renderEmptyState();
    }

    return `
      <div class="validation-panel-gain">
        <div class="gain-header">
          <h3 class="gain-title">Confirmation des Gains</h3>
          <span class="gain-count">${this.suspiciousGains.length} fibre(s)</span>
        </div>
        
        <div class="gain-list">
          ${this.suspiciousGains.map((gain) => this.renderGainItem(gain)).join("")}
        </div>
      </div>
    `;
  }

  /**
   * Rendu d'un gain suspect
   */
  renderGainItem(gain) {
    // Créer une clé unique avec référence + port
    const uniqueKey = gain.port ? `${gain.reference}-${gain.port}` : gain.reference;
    const confirmation = this.confirmations.get(uniqueKey);
    const isConfirmed = confirmation?.confirmed || false;
    const statusClass = isConfirmed ? "gain-item-confirmed" : "gain-item-pending";

    // Construire la référence complète avec le port formaté en 2 digits
    const fullReference = `${gain.reference}-${gain.port.toString().padStart(2, "0")}`;

    // Détails des mesures
    const measurementDetails = [];
    if (gain.measurements.pvReussiteCount > 0) {
      measurementDetails.push(`${gain.measurements.pvReussiteCount} PV Succès`);
    }
    if (gain.measurements.pvEchecCount > 0) {
      measurementDetails.push(`${gain.measurements.pvEchecCount} PV Échec`);
    }

    return `
      <div class="gain-item ${statusClass}" data-gain-id="${gain.id}">
        <div class="gain-item-header">
          <span class="gain-item-icon">${isConfirmed ? "✅" : "⚠️"}</span>
          <div class="gain-item-info">
            <span class="gain-item-reference">${fullReference}</span>
            <span class="gain-item-status">Déclarée occupée</span>
            <span class="gain-item-measurements">${measurementDetails.join(", ")}</span>
          </div>
        </div>
        
        <div class="gain-item-content">
          <div class="gain-explanation">
            <p class="gain-explanation-text">
              ❓ Cette fibre déclarée occupée possède des fichiers de mesures SOR. S'agit-il d'un gain confirmé ou d'une erreur de déclaration ?
            </p>
          </div>

          <div class="gain-actions">
            <label class="gain-checkbox-label">
              <input 
                type="checkbox" 
                class="gain-checkbox"
                data-fiber-reference="${gain.reference}"
                data-fiber-port="${gain.port}"
                ${isConfirmed ? "checked" : ""}
              />
              <span class="gain-checkbox-text">Gain Confirmé</span>
            </label>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * État vide
   */
  renderEmptyState() {
    return `
      <div class="validation-panel-gain">
        <div class="gain-header">
          <h3 class="gain-title">Confirmation des Gains</h3>
          <span class="gain-count">0 fibre</span>
        </div>
        
        <div class="gain-empty">
          <p class="gain-empty-text">Aucun gain suspect détecté</p>
        </div>
      </div>
    `;
  }

  /**
   * Confirmer un gain
   */
  confirmGain(fiberReference, fiberPort, confirmed) {
    // Créer une clé unique avec référence + port
    const uniqueKey = fiberPort ? `${fiberReference}-${fiberPort}` : fiberReference;

    // Commentaire automatique selon l'état
    const comment = confirmed ? "Gain confirmé - fibre mesurée avant raccordement client" : "";

    this.confirmations.set(uniqueKey, {
      confirmed: confirmed,
      comment: comment,
      port: fiberPort, // ← AJOUTER cette ligne
      timestamp: new Date(),
    });

    // Notifier le parent si callback fourni
    if (this.options.onGainConfirmed) {
      this.options.onGainConfirmed("gain", fiberReference, {
        confirmed,
        comment: comment,
        port: fiberPort,
      });
    }

    // Mettre à jour l'affichage
    this.updateItemStatus(fiberReference, fiberPort);
  }

  /**
   * Mettre à jour le statut visuel d'un item
   */
  updateItemStatus(fiberReference, fiberPort) {
    const gain = this.suspiciousGains.find((g) => g.reference === fiberReference && g.port == fiberPort);
    if (!gain) return;

    const item = this.element.querySelector(`[data-gain-id="${gain.id}"]`);
    if (!item) return;

    const uniqueKey = fiberPort ? `${fiberReference}-${fiberPort}` : fiberReference;
    const confirmation = this.confirmations.get(uniqueKey);
    const isConfirmed = confirmation?.confirmed || false;

    // Mettre à jour les classes CSS
    item.className = `gain-item ${isConfirmed ? "gain-item-confirmed" : "gain-item-pending"}`;

    // Mettre à jour l'icône
    const icon = item.querySelector(".gain-item-icon");
    if (icon) {
      icon.textContent = isConfirmed ? "✅" : "⚠️";
    }

    // Mettre à jour l'état de la checkbox
    const checkbox = item.querySelector(".gain-checkbox");
    if (checkbox) {
      checkbox.checked = isConfirmed;
    }
  }

  /**
   * Récupérer toutes les confirmations
   */
  getAllConfirmations() {
    const confirmations = [];

    this.suspiciousGains.forEach((gain) => {
      const uniqueKey = gain.port ? `${gain.reference}-${gain.port}` : gain.reference;
      const confirmation = this.confirmations.get(uniqueKey);
      if (confirmation && confirmation.confirmed) {
        confirmations.push({
          reference: gain.reference,
          port: gain.port,
          comment: confirmation.comment,
          type: "gain",
          confirmed: true,
        });
      }
    });

    return confirmations;
  }

  /**
   * Statistiques
   */
  getStats() {
    const total = this.suspiciousGains.length;
    const confirmed = this.getAllConfirmations().length;

    return {
      total,
      confirmed,
      pending: total - confirmed,
      percentage: total > 0 ? Math.round((confirmed / total) * 100) : 0,
    };
  }

  /**
   * Liaison des événements
   */
  bindEvents() {
    if (!this.element) return;

    // Événements sur les checkboxes
    this.element.querySelectorAll(".gain-checkbox").forEach((checkbox) => {
      checkbox.addEventListener("change", (e) => {
        const fiberReference = e.target.dataset.fiberReference;
        const fiberPort = e.target.dataset.fiberPort;
        const isChecked = e.target.checked;

        this.confirmGain(fiberReference, fiberPort, isChecked);
      });
    });
  }

  /**
   * Monter le composant
   */
  async mount(container) {
    try {
      await this.initialize();

      this.element = document.createElement("div");
      this.element.innerHTML = this.render();

      const mountedElement = this.element.firstElementChild;

      if (typeof container === "string") {
        const targetContainer = document.querySelector(container);
        if (targetContainer) {
          targetContainer.appendChild(mountedElement);
        } else {
          throw new Error(`Container not found: ${container}`);
        }
      } else if (container && container.appendChild) {
        container.appendChild(mountedElement);
      } else {
        throw new Error("Invalid container provided");
      }

      this.element = mountedElement;
      this.bindEvents();

      return this.element;
    } catch (error) {
      logger.error(
        {
          context: "panel-gain-mounting",
          error: error.message,
          stack: error.stack,
          containerType: typeof container,
          suspiciousGainsCount: this.suspiciousGains?.length || 0,
          hasElement: !!this.element,
        },
        "Erreur montage ValidationPanelGain"
      );
      throw error;
    }
  }

  /**
   * Démonter le composant
   */
  unmount() {
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
  }
}
